/**
 * Shared bottom-sheet / centered modal behaviour.
 *
 * One delegated, singleton controller for every modal in the theme that opts in
 * with data attributes — so the open/close, focus trap, ESC-to-close,
 * backdrop-tap-to-close and body scroll-lock live in exactly one place and are
 * never duplicated per component.
 *
 * Markup contract:
 *   - Modal root:   [data-sheet-modal="ID"]  (also give it id="ID")
 *   - Trigger:      [data-sheet-open="ID"]   (button/link anywhere in the page)
 *   - Close target: [data-sheet-close]       (close button AND the backdrop)
 *   - Autofocus:    [data-sheet-autofocus]    (element focused on open; optional)
 *
 * The controller toggles `aria-hidden` and an "open" class on the root. The open
 * class defaults to `sheet-modal--open` but can be overridden per modal with
 * `data-sheet-open-class` so a component can keep its own CSS hooks.
 *
 * Scroll-lock uses `body { overflow: hidden }` only — it never touches
 * `position`/`top`, so it is safe to open one of these on top of a native
 * <dialog> (e.g. the cart drawer) without fighting the dialog's own body lock.
 *
 * On open a `sheet:open` event and on close a `sheet:close` event are dispatched
 * on the modal root (bubbling) so content can lazily render itself.
 */
(() => {
  if (window.__sheetModalInit) return;
  window.__sheetModalInit = true;

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** @type {HTMLElement | null} */
  let activeModal = null;
  /** @type {HTMLElement | null} */
  let lastTrigger = null;

  const escapeId = (id) => (window.CSS && CSS.escape ? CSS.escape(id) : id);

  function getModal(id) {
    if (!id) return null;
    return (
      document.getElementById(id) ||
      document.querySelector(`[data-sheet-modal="${escapeId(id)}"]`)
    );
  }

  const openClassOf = (modal) => modal.dataset.sheetOpenClass || 'sheet-modal--open';

  function visibleFocusable(modal) {
    return Array.prototype.filter.call(
      modal.querySelectorAll(FOCUSABLE),
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  }

  function open(modal, trigger) {
    if (!modal || modal === activeModal) return;
    // Only one sheet modal open at a time.
    if (activeModal) close(activeModal);

    activeModal = modal;
    lastTrigger = trigger || null;

    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add(openClassOf(modal));
    // Promote to the top layer so it's positioned relative to the viewport (not a
    // transformed ancestor like the cart-drawer <dialog>) and stacks above it.
    // Only for modals that opt in via the `popover` attribute (e.g. the terms sheet).
    if (modal.hasAttribute('popover') && typeof modal.showPopover === 'function') {
      try {
        modal.showPopover();
      } catch (_) {}
    }
    document.body.style.overflow = 'hidden';

    modal.dispatchEvent(new CustomEvent('sheet:open', { bubbles: true }));

    requestAnimationFrame(() => {
      const target =
        modal.querySelector('[data-sheet-autofocus]') ||
        modal.querySelector('button[data-sheet-close]') ||
        visibleFocusable(modal)[0];
      // preventScroll: focusing must not scroll the drawer/page into view (mobile flicker).
      if (target) target.focus({ preventScroll: true });
    });
  }

  function close(modal) {
    modal = modal || activeModal;
    if (!modal) return;

    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove(openClassOf(modal));
    if (modal.hasAttribute('popover') && typeof modal.hidePopover === 'function') {
      try {
        modal.hidePopover();
      } catch (_) {}
    }

    const trigger = modal === activeModal ? lastTrigger : null;
    if (modal === activeModal) {
      activeModal = null;
      lastTrigger = null;
      document.body.style.overflow = '';
    }

    modal.dispatchEvent(new CustomEvent('sheet:close', { bubbles: true }));

    if (trigger && document.contains(trigger)) trigger.focus();
  }

  // Expose a tiny imperative API for any component that needs it.
  window.SheetModal = { open: (id, trigger) => open(getModal(id), trigger), close: () => close() };

  // Delegated open / close — survives Ajax cart morphs since it lives on document.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-sheet-open]');
    if (opener) {
      event.preventDefault();
      open(getModal(opener.getAttribute('data-sheet-open')), opener);
      return;
    }
    const closer = event.target.closest('[data-sheet-close]');
    if (closer) {
      event.preventDefault();
      close(closer.closest('[data-sheet-modal]'));
    }
  });

  // ESC-to-close + focus trap, scoped to whichever modal is open.
  document.addEventListener('keydown', (event) => {
    if (!activeModal) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close(activeModal);
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = visibleFocusable(activeModal);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first || !activeModal.contains(document.activeElement)) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last || !activeModal.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  });
})();
